import { useEffect, useState } from 'react';
import instance from '../../../../api/axios/utils.ts/instance';
import { dateToHyphen } from '../../../../api/utils/util-func';
import HFbutton from '../../../../components/common/input/HFbutton';
import CertificateImg from '../../../../components/mypage/CertificateImg';
import { ApiResponse } from '../../../../constants/types';
import {
  CertificateImgEnum,
  TeacherLicenseType,
} from '../../../../constants/types/mypage-type';
import { closeModal, openAlert } from '../../../../store/features/modalSlice';
import { useAppDispatch } from '../../../../store/hook';
import TextWinds from '../../../../components/common/TextWinds';
import { myLearnigDetailType } from '../../../../constants/types/learning-type';
import { Spin } from 'antd';
import HFinput from '../../../../components/common/input/HFinput';

interface CertificateModalType {
  coursedId: string;
  courseName: string;
  learningId: string;
  userEnglishName: string;
  type: CertificateImgEnum;
  readonly?: boolean;
  reloadData?: () => Promise<myLearnigDetailType>;
  licenseId?: number;
}

const MAX_NAME_LENGTH = 20;
const MIN_NAME_LENGTH = 2;

const CertificateModal = ({
  coursedId,
  courseName,
  learningId,
  userEnglishName,
  type,
  readonly = false,
  reloadData,
  licenseId,
}: CertificateModalType) => {
  const dispatch = useAppDispatch();
  const [ename, setEname] = useState<string>(userEnglishName);
  const [licenseData, setLicenseData] = useState<TeacherLicenseType | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(readonly);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  /**
   * 수료증 상세 조회
   * @param licenseId 수료증 ID
   * @returns 수료증 상세 데이터
   */
  const getTeacherLicenseById = async (licenseId: number) => {
    try {
      const response = await instance.get<ApiResponse<TeacherLicenseType>>(
        `/v1/mypage/teacher-license/${licenseId}`,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch license:', error);
      dispatch(
        openAlert({
          message: '수료증 정보를 불러오는데 실패했습니다.',
        }),
      );
      throw error;
    }
  };

  /**
   * 수료증 발급
   */
  const postTeacherLicense = async () => {
    try {
      const param = {
        coursedId: coursedId,
        courseName: courseName,
        learningId: learningId,
        userEnglishName: ename,
        graduationAt: new Date(),
      };
      await instance.post<ApiResponse<TeacherLicenseType[]>>(
        '/v1/mypage/teacher-license',
        param,
      );
    } catch (error) {
      console.error('Failed to generate license:', error);
      throw error;
    }
  };

  /**
   * 영문 이름 업데이트
   * @param ename 영문 이름
   * @returns 영문 이름 업데이트 결과
   */
  const updateUserEnglishName = async (ename: string) => {
    try {
      const response = await instance.put('v1/mypage/user', {
        englishName: ename,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update English name:', error);
      throw error;
    }
  };

  // 컴포넌트가 처음 마운트될 때 한번만 실행 (의존성배열 [])
  useEffect(() => {
    if (readonly && licenseId) {
      setIsLoading(true);
      getTeacherLicenseById(licenseId)
        .then((res) => {
          setLicenseData(res.data);
        })
        .catch(() => {
          dispatch(closeModal());
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  /**
   * 영문 이름 유효성 검사
   */
  const validateEnglishName = (name: string): string => {
    if (!name) return '';

    if (name.length < MIN_NAME_LENGTH) {
      return `영문 이름은 최소 ${MIN_NAME_LENGTH}자 이상이어야 합니다.`;
    }

    if (name.length > MAX_NAME_LENGTH) {
      return `영문 이름은 최대 ${MAX_NAME_LENGTH}자까지 가능합니다.`;
    }

    const isUpperCase = /^[A-Z\s]*$/.test(name);
    if (!isUpperCase) {
      return '영문 대문자만 입력 가능합니다.';
    }

    const hasOnlySpaces = /^\s*$/.test(name);
    if (hasOnlySpaces) {
      return '공백만으로는 이름을 구성할 수 없습니다.';
    }

    const hasConsecutiveSpaces = /\s{2,}/.test(name);
    if (hasConsecutiveSpaces) {
      return '연속된 공백은 사용할 수 없습니다.';
    }

    return '';
  };

  // 영문 이름 입력 시 유효성 검사
  const handleEnglishName = (name: string) => {
    setEname(name);
    setErrorMessage(validateEnglishName(name));
  };

  // 포커스 아웃 시 공백 제거
  const handleBlur = () => {
    const trimmedName = ename.trim();
    if (trimmedName !== ename) {
      setEname(trimmedName);
      setErrorMessage(validateEnglishName(trimmedName));
    }
  };

  /**
   * 수료증 발급 처리
   */
  const generateTeacherLicense = async () => {
    if (isGenerating) return; // 중복 클릭 방지

    try {
      setIsGenerating(true);

      // 최종 유효성 검사
      const validationError = validateEnglishName(ename);
      if (validationError) {
        dispatch(openAlert({ message: validationError }));
        return;
      }

      // 롤백을 위한 상태 저장
      let needsRollback = false;
      const originalName = licenseData?.userEnglishName;

      try {
        // 1. 영문 이름이 변경되었다면 업데이트
        if (ename !== licenseData?.userEnglishName) {
          await updateUserEnglishName(ename);
          needsRollback = true;
        }

        // 2. 수료증 발급
        await postTeacherLicense();

        // 3. 부모 컴포넌트의 데이터 갱신
        if (reloadData) await reloadData();

        // 4. 완료 메시지
        dispatch(openAlert({ message: '수료증이 발급되었습니다.' }));
      } catch (error) {
        // 실패 시 이름 롤백
        if (needsRollback && originalName) {
          try {
            await updateUserEnglishName(originalName);
          } catch (rollbackError) {
            console.error('Rollback failed:', rollbackError);
          }
        }
        throw error;
      }
    } catch (error) {
      console.error('License generation failed:', error);
      dispatch(
        openAlert({
          message: '수료증 발급 중 오류가 발생했습니다. 다시 시도해주세요.',
        }),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // 렌더링
  return (
    <div className="rounded bg-white p-5">
      <div className="relative">
        {/* 수료증 이미지 */}
        <CertificateImg
          name={licenseData?.userEnglishName || ename}
          type={licenseData?.licenseType || type}
          date={dateToHyphen(licenseData?.graduationAt || new Date())}
          readonly
        />
        {/* 로딩 스피너 */}
        {(isLoading || isGenerating) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <Spin />
          </div>
        )}
      </div>
      <div className="mt-5">
        {/* 영문 이름 입력필드 */}
        {!readonly && (
          <>
            <TextWinds type="content_body3">
              수료증 발급을 위해 영어이름을 확인해주세요.
            </TextWinds>
            <HFinput
              height={34}
              onChange={(e) => handleEnglishName(e.target.value)}
              onBlur={handleBlur}
              value={ename}
              placeholder="20자 이내 영문 대문자로 입력해주세요."
              error={errorMessage}
              className="mb-5"
            />
          </>
        )}
        <div className="flex flex-col gap-3">
          {/* 수료증 발급 버튼 */}
          {!readonly && (
            <HFbutton
              onClick={() => generateTeacherLicense()}
              disabled={isGenerating || !!errorMessage || !ename}
            >
              {isGenerating ? '발급 중...' : '수료증 발급'}
            </HFbutton>
          )}
          {/* 닫기 버튼 */}
          <HFbutton onClick={() => dispatch(closeModal())}>닫기</HFbutton>
        </div>
      </div>
    </div>
  );
};

export default CertificateModal;
